// src/pages/Products.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Package,
  Search,
  Plus,
  Edit3,
  Trash2,
  Percent,
  Shield,
  SlidersHorizontal,
  DollarSign,
  History,
  Calendar,
  RotateCcw,
  Sparkles,
  HelpCircle,
  Layers,
  ArrowRight,
  Settings,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  MoreHorizontal,
} from 'lucide-react';
import { Product, TierPermissions, ProductPriceHistory, CategoryPricingRule } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useToast } from '../contexts/ToastContext';
import { apiRequest } from '../lib/api';
import { formatDateTime } from '../lib/date';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Pagination } from '../components/ui/Pagination';
import { TableSkeleton } from '../components/ui/TableSkeleton';
import { EmptyState } from '../components/ui/EmptyState';

export const Products: React.FC = () => {
  const { user, can } = useAuth();
  const { settings } = useSettings();
  const { success, error: toastError } = useToast();

  const [products, setProducts] = useState<Product[]>([]);
  const [tierPerms, setTierPerms] = useState<TierPermissions>({
    canViewCost: false,
    canViewTraders: false,
    canViewSubCont: false,
    canViewMainCont: false,
    canViewClient: false,
    canEditCost: false,
    canEditTraders: false,
    canEditSubCont: false,
    canEditMainCont: false,
    canEditClient: false,
  });

  const [categoryRules, setCategoryRules] = useState<CategoryPricingRule[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<{ [key: string]: number }>({ all: 59, Manufacturing: 44, 'Trading Products': 15 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 1 });

  // Category Pricing Rules Modal State
  const [catRulesModalOpen, setCatRulesModalOpen] = useState(false);
  const [selectedCatRule, setSelectedCatRule] = useState<string>('ALL');
  const [catRuleForm, setCatRuleForm] = useState({
    net_cost_pct: '23',
    traders_pct: '25',
    sub_cont_pct: '35',
    main_cont_pct: '40',
    client_pct: '50',
    recalculate_products: true,
  });
  const [savingCatRule, setSavingCatRule] = useState(false);

  // Quick Percentage Modal State (Single Product)
  const [quickPctProduct, setQuickPctProduct] = useState<Product | null>(null);
  const [quickPctForm, setQuickPctForm] = useState({
    cost: '',
    net_cost_pct: '',
    traders_pct: '',
    sub_cont_pct: '',
    main_cont_pct: '',
    client_pct: '',
    allow_low_margin: false,
    margin_override_reason: '',
  });
  const [savingQuickPct, setSavingQuickPct] = useState(false);

  // Edit Pricing Modal State (Comprehensive)
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [editPrices, setEditPrices] = useState({
    name: '',
    category: 'Manufacturing',
    unit: '',
    cost: '',
    net_cost: '',
    traders_price: '',
    sub_cont_price: '',
    main_cont_price: '',
    client_price: '',
    net_cost_pct: '',
    traders_pct: '',
    sub_cont_pct: '',
    main_cont_pct: '',
    client_pct: '',
    effective_month: '',
    change_reason: '',
    allow_low_margin: false,
    margin_override_reason: '',
  });
  const [savingPrice, setSavingPrice] = useState(false);

  // Price History Modal State
  const [historyProduct, setHistoryProduct] = useState<Product | null>(null);
  const [historyRecords, setHistoryRecords] = useState<ProductPriceHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Add Product Modal State
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    category: 'Manufacturing',
    unit: '20 LTR',
    cost: '',
    net_cost: '',
    traders_price: '',
    sub_cont_price: '',
    main_cont_price: '',
    client_price: '',
    net_cost_pct: '',
    traders_pct: '',
    sub_cont_pct: '',
    main_cont_pct: '',
    client_pct: '',
    effective_month: '',
    change_reason: '',
    allow_low_margin: false,
    margin_override_reason: '',
  });
  const [addingProduct, setAddingProduct] = useState(false);

  // Delete Confirmation State
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; product: Product | null; loading: boolean }>({
    open: false,
    product: null,
    loading: false,
  });

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        pageSize: '50',
      });
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);

      const res = await apiRequest<{
        items: Product[];
        pagination: any;
        tierPermissions: TierPermissions;
        categoryPricingRules: CategoryPricingRule[];
        categoryCounts?: { [key: string]: number };
      }>(`/api/products?${params.toString()}`);

      setProducts(res.items || []);
      setPagination(res.pagination);
      if (res.tierPermissions) {
        setTierPerms(res.tierPermissions);
      }
      if (res.categoryPricingRules) {
        setCategoryRules(res.categoryPricingRules);
      }
      if (res.categoryCounts) {
        setCategoryCounts(res.categoryCounts);
      }
    } catch (err: any) {
      toastError(err.message || 'Failed to load products.');
    } finally {
      setLoading(false);
    }
  }, [page, search, categoryFilter, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadProducts();
    }, 200);
    return () => clearTimeout(timer);
  }, [loadProducts]);

  // Load category pricing rules independently
  const loadCategoryRules = async () => {
    try {
      const res = await apiRequest<{ rules: CategoryPricingRule[] }>('/api/products/pricing-rules');
      if (res?.rules) {
        setCategoryRules(res.rules);
        if (selectedCatRule !== 'ALL') {
          const currentRule = res.rules.find(r => r.category === selectedCatRule) || res.rules[0];
          if (currentRule) {
            setSelectedCatRule(currentRule.category);
            setCatRuleForm({
              net_cost_pct: String(currentRule.net_cost_pct ?? 23),
              traders_pct: String(currentRule.traders_pct ?? 25),
              sub_cont_pct: String(currentRule.sub_cont_pct ?? 35),
              main_cont_pct: String(currentRule.main_cont_pct ?? 40),
              client_pct: String(currentRule.client_pct ?? 50),
              recalculate_products: true,
            });
          }
        }
      }
    } catch (err: any) {
      console.warn('Failed to load category rules', err);
    }
  };

  const handleOpenCatRules = () => {
    loadCategoryRules();
    setSelectedCatRule('ALL');
    setCatRulesModalOpen(true);
  };

  const handleSelectCategoryForRule = (cat: string) => {
    setSelectedCatRule(cat);
    if (cat === 'ALL') {
      const defaultRule = categoryRules[0];
      if (defaultRule) {
        setCatRuleForm(prev => ({
          ...prev,
          net_cost_pct: String(defaultRule.net_cost_pct ?? 23),
          traders_pct: String(defaultRule.traders_pct ?? 25),
          sub_cont_pct: String(defaultRule.sub_cont_pct ?? 35),
          main_cont_pct: String(defaultRule.main_cont_pct ?? 40),
          client_pct: String(defaultRule.client_pct ?? 50),
          recalculate_products: true,
        }));
      }
      return;
    }
    const rule = categoryRules.find(r => r.category === cat);
    if (rule) {
      setCatRuleForm({
        net_cost_pct: String(rule.net_cost_pct ?? 23),
        traders_pct: String(rule.traders_pct ?? 25),
        sub_cont_pct: String(rule.sub_cont_pct ?? 35),
        main_cont_pct: String(rule.main_cont_pct ?? 40),
        client_pct: String(rule.client_pct ?? 50),
        recalculate_products: true,
      });
    } else {
      setCatRuleForm({
        net_cost_pct: '23',
        traders_pct: '25',
        sub_cont_pct: '35',
        main_cont_pct: '40',
        client_pct: '50',
        recalculate_products: true,
      });
    }
  };

  const handleSaveCatRule = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingCatRule(true);
    try {
      const isAll = selectedCatRule === 'ALL' || selectedCatRule === 'all';
      const res = await apiRequest<any>('/api/products/pricing-rules', {
        method: 'PUT',
        body: JSON.stringify({
          category: selectedCatRule,
          apply_to_all_categories: isAll,
          net_cost_pct: Number(catRuleForm.net_cost_pct),
          traders_pct: Number(catRuleForm.traders_pct),
          sub_cont_pct: Number(catRuleForm.sub_cont_pct),
          main_cont_pct: Number(catRuleForm.main_cont_pct),
          client_pct: Number(catRuleForm.client_pct),
          recalculate_products: catRuleForm.recalculate_products,
        }),
      });

      success(
        catRuleForm.recalculate_products
          ? `Pricing rules saved and ${res.updatedProductsCount || 0} products recalculated successfully!`
          : `Pricing rules for ${isAll ? 'all categories' : `"${selectedCatRule}"`} updated successfully.`
      );
      setCatRulesModalOpen(false);
      loadProducts();
    } catch (err: any) {
      toastError(err.message || 'Failed to update category pricing rules.');
    } finally {
      setSavingCatRule(false);
    }
  };

  // Helper to get category rule
  const getRuleForCategory = (cat: string) => {
    return categoryRules.find(r => r.category === cat) || {
      category: cat,
      net_cost_pct: 23,
      traders_pct: 25,
      sub_cont_pct: 35,
      main_cont_pct: 40,
      client_pct: 50,
    };
  };

  // Helper calculation
  const calculateTierPrices = (
    costVal: number,
    netPct: number,
    traderPct: number,
    subPct: number,
    mainPct: number,
    clientPct: number
  ) => {
    const round2 = (num: number) => Math.round((num + Number.EPSILON) * 100) / 100;
    const net_cost = round2(costVal * (1 + (netPct || 0) / 100));
    const traders_price = round2(net_cost * (1 + (traderPct || 0) / 100));
    const sub_cont_price = round2(net_cost * (1 + (subPct || 0) / 100));
    const main_cont_price = round2(net_cost * (1 + (mainPct || 0) / 100));
    const client_price = round2(net_cost * (1 + (clientPct || 0) / 100));
    return { net_cost, traders_price, sub_cont_price, main_cont_price, client_price };
  };

  // Quick Percentage Edit Modal Handler
  const handleOpenQuickPct = (p: Product) => {
    setQuickPctProduct(p);
    const catRule = getRuleForCategory(p.category);
    setQuickPctForm({
      cost: p.cost !== undefined ? String(p.cost) : '0',
      net_cost_pct: p.net_cost_pct !== null && p.net_cost_pct !== undefined ? String(p.net_cost_pct) : '',
      traders_pct: p.traders_pct !== null && p.traders_pct !== undefined ? String(p.traders_pct) : '',
      sub_cont_pct: p.sub_cont_pct !== null && p.sub_cont_pct !== undefined ? String(p.sub_cont_pct) : '',
      main_cont_pct: p.main_cont_pct !== null && p.main_cont_pct !== undefined ? String(p.main_cont_pct) : '',
      client_pct: p.client_pct !== null && p.client_pct !== undefined ? String(p.client_pct) : '',
      allow_low_margin: false,
      margin_override_reason: '',
    });
  };

  const handleSaveQuickPct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickPctProduct) return;

    setSavingQuickPct(true);
    try {
      const payload: any = {
        auto_calculate: true,
      };

      if (tierPerms.canEditCost) {
        if (quickPctForm.cost !== '') payload.cost = Number(quickPctForm.cost);
        payload.net_cost_pct = quickPctForm.net_cost_pct !== '' ? Number(quickPctForm.net_cost_pct) : null;
      }
      if (tierPerms.canEditTraders) {
        payload.traders_pct = quickPctForm.traders_pct !== '' ? Number(quickPctForm.traders_pct) : null;
      }
      if (tierPerms.canEditSubCont) {
        payload.sub_cont_pct = quickPctForm.sub_cont_pct !== '' ? Number(quickPctForm.sub_cont_pct) : null;
      }
      if (tierPerms.canEditMainCont) {
        payload.main_cont_pct = quickPctForm.main_cont_pct !== '' ? Number(quickPctForm.main_cont_pct) : null;
      }
      if (tierPerms.canEditClient) {
        payload.client_pct = quickPctForm.client_pct !== '' ? Number(quickPctForm.client_pct) : null;
      }
      if (quickPctForm.allow_low_margin) {
        payload.allow_low_margin = true;
      }
      if (quickPctForm.margin_override_reason) {
        payload.change_reason = quickPctForm.margin_override_reason;
      }

      await apiRequest(`/api/products/${quickPctProduct.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      success(`Percentages and prices updated for "${quickPctProduct.name}".`);
      setQuickPctProduct(null);
      loadProducts();
    } catch (err: any) {
      toastError(err.message || 'Failed to update percentages.');
    } finally {
      setSavingQuickPct(false);
    }
  };

  // Comprehensive Edit Pricing Modal
  const handleOpenEdit = (p: Product) => {
    setEditProduct(p);
    const currentMonth = new Date().toISOString().substring(0, 7);
    setEditPrices({
      name: p.name,
      category: p.category,
      unit: p.unit || '',
      cost: p.cost !== undefined ? String(p.cost) : '',
      net_cost: p.net_cost !== undefined ? String(p.net_cost) : '',
      traders_price: p.traders_price !== undefined ? String(p.traders_price) : '',
      sub_cont_price: p.sub_cont_price !== undefined ? String(p.sub_cont_price) : '',
      main_cont_price: p.main_cont_price !== undefined ? String(p.main_cont_price) : '',
      client_price: p.client_price !== undefined ? String(p.client_price) : '',
      net_cost_pct: p.net_cost_pct !== null && p.net_cost_pct !== undefined ? String(p.net_cost_pct) : '',
      traders_pct: p.traders_pct !== null && p.traders_pct !== undefined ? String(p.traders_pct) : '',
      sub_cont_pct: p.sub_cont_pct !== null && p.sub_cont_pct !== undefined ? String(p.sub_cont_pct) : '',
      main_cont_pct: p.main_cont_pct !== null && p.main_cont_pct !== undefined ? String(p.main_cont_pct) : '',
      client_pct: p.client_pct !== null && p.client_pct !== undefined ? String(p.client_pct) : '',
      effective_month: currentMonth,
      change_reason: 'Monthly Price Revision',
      allow_low_margin: false,
      margin_override_reason: '',
    });
  };

  // When changing Cost or percentages in Edit modal, offer live recalculation
  const handleRecalculateEditPrices = () => {
    if (!editProduct) return;
    const catRule = getRuleForCategory(editPrices.category);
    const costVal = Number(editPrices.cost || 0);
    const netPct = editPrices.net_cost_pct !== '' ? Number(editPrices.net_cost_pct) : catRule.net_cost_pct;
    const traderPct = editPrices.traders_pct !== '' ? Number(editPrices.traders_pct) : catRule.traders_pct;
    const subPct = editPrices.sub_cont_pct !== '' ? Number(editPrices.sub_cont_pct) : catRule.sub_cont_pct;
    const mainPct = editPrices.main_cont_pct !== '' ? Number(editPrices.main_cont_pct) : catRule.main_cont_pct;
    const clientPct = editPrices.client_pct !== '' ? Number(editPrices.client_pct) : catRule.client_pct;

    const calc = calculateTierPrices(costVal, netPct, traderPct, subPct, mainPct, clientPct);
    setEditPrices(prev => ({
      ...prev,
      net_cost: String(calc.net_cost),
      traders_price: String(calc.traders_price),
      sub_cont_price: String(calc.sub_cont_price),
      main_cont_price: String(calc.main_cont_price),
      client_price: String(calc.client_price),
    }));
  };

  const handleSavePricing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editProduct) return;

    setSavingPrice(true);
    try {
      const payload: any = {
        name: editPrices.name,
        category: editPrices.category,
        unit: editPrices.unit,
      };

      if (tierPerms.canEditCost) {
        if (editPrices.cost !== '') payload.cost = Number(editPrices.cost);
        if (editPrices.net_cost !== '') payload.net_cost = Number(editPrices.net_cost);
        payload.net_cost_pct = editPrices.net_cost_pct !== '' ? Number(editPrices.net_cost_pct) : null;
      }
      if (tierPerms.canEditTraders) {
        if (editPrices.traders_price !== '') payload.traders_price = Number(editPrices.traders_price);
        payload.traders_pct = editPrices.traders_pct !== '' ? Number(editPrices.traders_pct) : null;
      }
      if (tierPerms.canEditSubCont) {
        if (editPrices.sub_cont_price !== '') payload.sub_cont_price = Number(editPrices.sub_cont_price);
        payload.sub_cont_pct = editPrices.sub_cont_pct !== '' ? Number(editPrices.sub_cont_pct) : null;
      }
      if (tierPerms.canEditMainCont) {
        if (editPrices.main_cont_price !== '') payload.main_cont_price = Number(editPrices.main_cont_price);
        payload.main_cont_pct = editPrices.main_cont_pct !== '' ? Number(editPrices.main_cont_pct) : null;
      }
      if (tierPerms.canEditClient) {
        if (editPrices.client_price !== '') payload.client_price = Number(editPrices.client_price);
        payload.client_pct = editPrices.client_pct !== '' ? Number(editPrices.client_pct) : null;
      }
      if (editPrices.effective_month) {
        payload.effective_month = editPrices.effective_month;
      }
      if (editPrices.change_reason) {
        payload.change_reason = editPrices.change_reason;
      }
      if (editPrices.allow_low_margin) {
        payload.allow_low_margin = true;
      }
      if (editPrices.margin_override_reason) {
        payload.change_reason = `${editPrices.change_reason || 'Price Update'} (Override: ${editPrices.margin_override_reason})`;
      }

      await apiRequest(`/api/products/${editProduct.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      success(`Pricing for "${editProduct.name}" updated successfully.`);
      setEditProduct(null);
      loadProducts();
    } catch (err: any) {
      toastError(err.message || 'Failed to update pricing.');
    } finally {
      setSavingPrice(false);
    }
  };

  const handleOpenAddProduct = () => {
    const cat = 'Manufacturing';
    const catRule = getRuleForCategory(cat);
    setNewProduct({
      name: '',
      category: cat,
      unit: '20 LTR',
      cost: '',
      net_cost: '',
      traders_price: '',
      sub_cont_price: '',
      main_cont_price: '',
      client_price: '',
      net_cost_pct: '',
      traders_pct: '',
      sub_cont_pct: '',
      main_cont_pct: '',
      client_pct: '',
      effective_month: new Date().toISOString().substring(0, 7),
      change_reason: 'Initial Product Creation',
      allow_low_margin: false,
      margin_override_reason: '',
    });
    setAddModalOpen(true);
  };

  const handleAddProductCostChange = (costValStr: string, catStr: string = newProduct.category) => {
    const catRule = getRuleForCategory(catStr);
    const costVal = Number(costValStr || 0);
    const netPct = newProduct.net_cost_pct !== '' ? Number(newProduct.net_cost_pct) : catRule.net_cost_pct;
    const traderPct = newProduct.traders_pct !== '' ? Number(newProduct.traders_pct) : catRule.traders_pct;
    const subPct = newProduct.sub_cont_pct !== '' ? Number(newProduct.sub_cont_pct) : catRule.sub_cont_pct;
    const mainPct = newProduct.main_cont_pct !== '' ? Number(newProduct.main_cont_pct) : catRule.main_cont_pct;
    const clientPct = newProduct.client_pct !== '' ? Number(newProduct.client_pct) : catRule.client_pct;

    const calc = calculateTierPrices(costVal, netPct, traderPct, subPct, mainPct, clientPct);
    setNewProduct(prev => ({
      ...prev,
      cost: costValStr,
      category: catStr,
      net_cost: costVal > 0 ? String(calc.net_cost) : '',
      traders_price: costVal > 0 ? String(calc.traders_price) : '',
      sub_cont_price: costVal > 0 ? String(calc.sub_cont_price) : '',
      main_cont_price: costVal > 0 ? String(calc.main_cont_price) : '',
      client_price: costVal > 0 ? String(calc.client_price) : '',
    }));
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProduct.name) {
      toastError('Product name is required.');
      return;
    }

    setAddingProduct(true);
    try {
      const payload: any = {
        name: newProduct.name,
        category: newProduct.category,
        unit: newProduct.unit,
        cost: Number(newProduct.cost || 0),
        net_cost: Number(newProduct.net_cost || 0),
        traders_price: Number(newProduct.traders_price || 0),
        sub_cont_price: Number(newProduct.sub_cont_price || 0),
        main_cont_price: Number(newProduct.main_cont_price || 0),
        client_price: Number(newProduct.client_price || 0),
      };
      if (newProduct.net_cost_pct !== '') payload.net_cost_pct = Number(newProduct.net_cost_pct);
      if (newProduct.traders_pct !== '') payload.traders_pct = Number(newProduct.traders_pct);
      if (newProduct.sub_cont_pct !== '') payload.sub_cont_pct = Number(newProduct.sub_cont_pct);
      if (newProduct.main_cont_pct !== '') payload.main_cont_pct = Number(newProduct.main_cont_pct);
      if (newProduct.client_pct !== '') payload.client_pct = Number(newProduct.client_pct);

      if (newProduct.effective_month) payload.effective_month = newProduct.effective_month;
      if (newProduct.change_reason) payload.change_reason = newProduct.change_reason;
      if (newProduct.allow_low_margin) payload.allow_low_margin = true;
      if (newProduct.margin_override_reason) {
        payload.change_reason = `${newProduct.change_reason || 'Initial Creation'} (Override: ${newProduct.margin_override_reason})`;
      }

      await apiRequest('/api/products', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      success(`Product "${newProduct.name}" created successfully.`);
      setAddModalOpen(false);
      loadProducts();
    } catch (err: any) {
      toastError(err.message || 'Failed to add product.');
    } finally {
      setAddingProduct(false);
    }
  };

  const handleOpenHistory = async (p: Product) => {
    setHistoryProduct(p);
    setHistoryLoading(true);
    try {
      const res = await apiRequest<{ product: any; history: ProductPriceHistory[] }>(`/api/products/${p.id}/history`);
      setHistoryRecords(res.history || []);
    } catch (err: any) {
      toastError(err.message || 'Failed to load price history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!confirmDelete.product) return;
    setConfirmDelete(prev => ({ ...prev, loading: true }));
    try {
      await apiRequest(`/api/products/${confirmDelete.product.id}`, { method: 'DELETE' });
      success(`Product "${confirmDelete.product.name}" archived.`);
      setConfirmDelete({ open: false, product: null, loading: false });
      loadProducts();
    } catch (err: any) {
      toastError(err.message || 'Failed to delete product.');
      setConfirmDelete(prev => ({ ...prev, loading: false }));
    }
  };

  const formatPrice = (val?: number) => {
    if (val === undefined || val === null) return '—';
    return `AED ${val.toFixed(2)}`;
  };

  return (
    <div className="space-y-6">
      {/* Title & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-slate-900">Products & Multi-Tier Pricing</h2>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center gap-1">
              <History className="w-3 h-3 text-emerald-600" />
              Formula & Tier Matrix Active
            </span>
          </div>
          <p className="text-xs text-slate-500">
            UAE Region (AED) • Enterprise catalog with category-level markup rules and individual product percentage overrides
          </p>
        </div>

        <div className="flex items-center gap-2">
          {tierPerms.canEditCost && (
            <Button
              variant="outline"
              onClick={handleOpenCatRules}
              icon={<SlidersHorizontal className="w-4 h-4 text-indigo-600" />}
            >
              Category % Rules
            </Button>
          )}

          {can('products.create') && (
            <Button onClick={handleOpenAddProduct} icon={<Plus className="w-4 h-4" />}>
              Add Product
            </Button>
          )}
        </div>
      </div>

      {/* Permission Scope Banner */}
      <div className="bg-slate-900 text-slate-200 p-4 rounded-xl border border-slate-800 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-indigo-600/30 text-indigo-400 border border-indigo-500/30 mt-0.5">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-white">
              Role-Based Pricing Visibility: {user?.role?.name}
            </h4>
            <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
              {tierPerms.canViewCost
                ? '✓ You have full visibility into confidential manufacturing Cost, Net Cost, and profit margins.'
                : '🔒 Confidential internal manufacturing costs and profit margins are redacted for your role.'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono">
          <span className={`px-2 py-1 rounded border ${tierPerms.canViewCost ? 'bg-emerald-950/60 border-emerald-700 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-500'}`}>
            Cost: {tierPerms.canViewCost ? 'Visible' : 'Hidden'}
          </span>
          <span className={`px-2 py-1 rounded border ${tierPerms.canViewTraders ? 'bg-emerald-950/60 border-emerald-700 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-500'}`}>
            Traders: {tierPerms.canViewTraders ? 'Visible' : 'Hidden'}
          </span>
          <span className={`px-2 py-1 rounded border ${tierPerms.canViewSubCont ? 'bg-emerald-950/60 border-emerald-700 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-500'}`}>
            SubCont: {tierPerms.canViewSubCont ? 'Visible' : 'Hidden'}
          </span>
          <span className={`px-2 py-1 rounded border ${tierPerms.canViewMainCont ? 'bg-emerald-950/60 border-emerald-700 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-500'}`}>
            MainCont: {tierPerms.canViewMainCont ? 'Visible' : 'Hidden'}
          </span>
          <span className={`px-2 py-1 rounded border ${tierPerms.canViewClient ? 'bg-emerald-950/60 border-emerald-700 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-500'}`}>
            Client: {tierPerms.canViewClient ? 'Visible' : 'Hidden'}
          </span>
        </div>
      </div>

      {/* Category Tabs & Search */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col sm:flex-row gap-4 justify-between items-center">
        {/* Category Pills */}
        <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-lg w-full sm:w-auto">
          <button
            type="button"
            onClick={() => {
              setCategoryFilter('');
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
              categoryFilter === '' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            All Products ({categoryCounts.all || 59})
          </button>
          <button
            type="button"
            onClick={() => {
              setCategoryFilter('Manufacturing');
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
              categoryFilter === 'Manufacturing' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Manufacturing ({categoryCounts['Manufacturing'] ?? 44})
          </button>
          <button
            type="button"
            onClick={() => {
              setCategoryFilter('Trading Products');
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
              categoryFilter === 'Trading Products' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Trading Products ({categoryCounts['Trading Products'] ?? 15})
          </button>
        </div>

        {/* Search */}
        <div className="w-full sm:w-80">
          <Input
            placeholder="Search product name or #..."
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            icon={<Search className="w-4 h-4" />}
          />
        </div>
      </div>

      {/* Multi-Tier Pricing Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <TableSkeleton rows={10} columns={tierPerms.canViewCost ? 8 : 6} />
        ) : products.length === 0 ? (
          <EmptyState
            icon={<Package className="w-10 h-10 text-slate-400" />}
            title="No products found"
            description="No products matched your search or category filter."
          />
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 text-center w-10 text-slate-400 font-mono">#</th>
                    <th className="px-4 py-3 text-left min-w-[200px]">Product</th>
                    <th className="px-3 py-3 text-left w-28 whitespace-nowrap">Category</th>
                    <th className="px-3 py-3 text-right w-24 whitespace-nowrap text-slate-700 font-bold">
                      Stock
                      <span className="block text-[9px] text-slate-400 font-normal lowercase tracking-normal">available</span>
                    </th>

                    {/* Sensitive Cost Tiers */}
                    {tierPerms.canViewCost && (
                      <>
                        <th className="px-3 py-3 text-right bg-amber-50/50 text-amber-950 font-bold whitespace-nowrap">
                          Cost (AED)
                        </th>
                        <th className="px-3 py-3 text-right bg-amber-50/50 text-amber-950 font-bold whitespace-nowrap">
                          Net Cost (AED)
                          <span className="block text-[9px] text-amber-700/80 font-normal lowercase tracking-normal">on cost</span>
                        </th>
                      </>
                    )}

                    {/* Selling Tiers */}
                    {tierPerms.canViewTraders && (
                      <th className="px-3 py-3 text-right text-indigo-950 font-bold whitespace-nowrap">
                        Traders (AED)
                        <span className="block text-[9px] text-indigo-500 font-normal lowercase tracking-normal">on net</span>
                      </th>
                    )}
                    {tierPerms.canViewSubCont && (
                      <th className="px-3 py-3 text-right text-indigo-950 font-bold whitespace-nowrap">
                        Sub Cont (AED)
                        <span className="block text-[9px] text-indigo-500 font-normal lowercase tracking-normal">on net</span>
                      </th>
                    )}
                    {tierPerms.canViewMainCont && (
                      <th className="px-3 py-3 text-right text-indigo-950 font-bold whitespace-nowrap">
                        Main Cont (AED)
                        <span className="block text-[9px] text-indigo-500 font-normal lowercase tracking-normal">on net</span>
                      </th>
                    )}
                    {tierPerms.canViewClient && (
                      <th className="px-3 py-3 text-right bg-emerald-50/40 text-emerald-950 font-bold whitespace-nowrap">
                        Client (AED)
                        <span className="block text-[9px] text-emerald-600 font-normal lowercase tracking-normal">on net</span>
                      </th>
                    )}

                    <th className="px-3 py-3 text-center w-28 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {products.map(p => (
                    <tr key={p.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-3 py-3 text-center font-mono text-xs text-slate-400 font-medium">
                        {p.item_no}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900 text-sm leading-tight">{p.name}</span>
                            {tierPerms.canViewCost && p.margin_status && (
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                  p.margin_status === 'healthy'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : p.margin_status === 'warning'
                                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                                    : 'bg-rose-50 text-rose-700 border-rose-200 animate-pulse'
                                }`}
                                title={
                                  p.lowest_margin !== undefined
                                    ? `Lowest Gross Margin: ${p.lowest_margin}% (${p.lowest_margin_tier || ''})`
                                    : 'Margin Floor Protection'
                                }
                              >
                                {p.margin_status === 'healthy' ? (
                                  <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600 shrink-0" />
                                ) : p.margin_status === 'warning' ? (
                                  <AlertTriangle className="w-2.5 h-2.5 text-amber-600 shrink-0" />
                                ) : (
                                  <AlertCircle className="w-2.5 h-2.5 text-rose-600 shrink-0" />
                                )}
                                <span className="tabular-nums">{p.lowest_margin !== undefined ? `${p.lowest_margin}% margin` : p.margin_status}</span>
                              </span>
                            )}
                          </div>
                          {(p.has_custom_net_cost_pct ||
                            p.has_custom_traders_pct ||
                            p.has_custom_sub_cont_pct ||
                            p.has_custom_main_cont_pct ||
                            p.has_custom_client_pct) && (
                            <div>
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200">
                                <Sparkles className="w-2.5 h-2.5 text-amber-500" />
                                Custom Override
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-700">{p.category}</span>
                          {p.unit && (
                            <span className="text-[10px] text-slate-400 font-mono">
                              {p.unit}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Physical Stock Balance (Aligned with Sales Team sheet) */}
                      <td className="px-3 py-3 text-right whitespace-nowrap font-mono text-xs">
                        {p.stock_balance !== undefined && p.stock_balance > 0 ? (
                          <span className="font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                            {p.stock_balance.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </td>

                      {/* Cost Tiers */}
                      {tierPerms.canViewCost && (
                        <>
                          <td className="px-3 py-3 font-mono text-xs font-semibold text-slate-800 bg-amber-50/20 text-right whitespace-nowrap">
                            {formatPrice(p.cost)}
                          </td>
                          <td className="px-3 py-3 font-mono text-xs font-bold text-slate-900 bg-amber-50/20 text-right whitespace-nowrap">
                            <div>{formatPrice(p.net_cost)}</div>
                            {p.effective_net_cost_pct !== undefined && (
                              <div className="mt-0.5">
                                <span
                                  className={`inline-block text-[10px] font-sans font-medium px-1.5 py-0.2 rounded ${
                                    p.has_custom_net_cost_pct
                                      ? 'text-amber-800 bg-amber-100/80 border border-amber-200 font-bold'
                                      : 'text-amber-700/80 bg-amber-50'
                                  }`}
                                  title={p.has_custom_net_cost_pct ? 'Custom product override' : 'Category default rule'}
                                >
                                  +{p.effective_net_cost_pct}%
                                </span>
                              </div>
                            )}
                          </td>
                        </>
                      )}

                      {/* Traders Tier */}
                      {tierPerms.canViewTraders && (
                        <td className="px-3 py-3 font-mono text-xs text-slate-800 text-right whitespace-nowrap">
                          <span className="font-bold text-slate-900">{formatPrice(p.traders_price)}</span>
                          <div className="flex items-center justify-end gap-1.5 mt-0.5 font-sans">
                            {p.effective_traders_pct !== undefined && (
                              <span
                                className={`text-[10px] px-1 py-0.2 rounded font-medium ${
                                  p.has_custom_traders_pct
                                    ? 'text-indigo-800 bg-indigo-100/70 border border-indigo-200 font-bold'
                                    : 'text-indigo-600'
                                }`}
                                title={p.has_custom_traders_pct ? 'Custom product override' : 'Category default rule'}
                              >
                                +{p.effective_traders_pct}%
                              </span>
                            )}
                            {tierPerms.canViewCost && p.traders_margin !== undefined && (
                              <span className="text-[10px] text-slate-400">({p.traders_margin}%)</span>
                            )}
                          </div>
                        </td>
                      )}

                      {/* Sub Cont Tier */}
                      {tierPerms.canViewSubCont && (
                        <td className="px-3 py-3 font-mono text-xs text-slate-800 text-right whitespace-nowrap">
                          <span className="font-bold text-slate-900">{formatPrice(p.sub_cont_price)}</span>
                          <div className="flex items-center justify-end gap-1.5 mt-0.5 font-sans">
                            {p.effective_sub_cont_pct !== undefined && (
                              <span
                                className={`text-[10px] px-1 py-0.2 rounded font-medium ${
                                  p.has_custom_sub_cont_pct
                                    ? 'text-indigo-800 bg-indigo-100/70 border border-indigo-200 font-bold'
                                    : 'text-indigo-600'
                                }`}
                                title={p.has_custom_sub_cont_pct ? 'Custom product override' : 'Category default rule'}
                              >
                                +{p.effective_sub_cont_pct}%
                              </span>
                            )}
                            {tierPerms.canViewCost && p.sub_cont_margin !== undefined && (
                              <span className="text-[10px] text-slate-400">({p.sub_cont_margin}%)</span>
                            )}
                          </div>
                        </td>
                      )}

                      {/* Main Cont Tier */}
                      {tierPerms.canViewMainCont && (
                        <td className="px-3 py-3 font-mono text-xs text-slate-800 text-right whitespace-nowrap">
                          <span className="font-bold text-slate-900">{formatPrice(p.main_cont_price)}</span>
                          <div className="flex items-center justify-end gap-1.5 mt-0.5 font-sans">
                            {p.effective_main_cont_pct !== undefined && (
                              <span
                                className={`text-[10px] px-1 py-0.2 rounded font-medium ${
                                  p.has_custom_main_cont_pct
                                    ? 'text-indigo-800 bg-indigo-100/70 border border-indigo-200 font-bold'
                                    : 'text-indigo-600'
                                }`}
                                title={p.has_custom_main_cont_pct ? 'Custom product override' : 'Category default rule'}
                              >
                                +{p.effective_main_cont_pct}%
                              </span>
                            )}
                            {tierPerms.canViewCost && p.main_cont_margin !== undefined && (
                              <span className="text-[10px] text-slate-400">({p.main_cont_margin}%)</span>
                            )}
                          </div>
                        </td>
                      )}

                      {/* Client Direct Tier */}
                      {tierPerms.canViewClient && (
                        <td className="px-3 py-3 font-mono text-xs bg-emerald-50/20 text-right whitespace-nowrap">
                          <span className="font-bold text-emerald-800">{formatPrice(p.client_price)}</span>
                          <div className="flex items-center justify-end gap-1.5 mt-0.5 font-sans">
                            {p.effective_client_pct !== undefined && (
                              <span
                                className={`text-[10px] px-1 py-0.2 rounded font-medium ${
                                  p.has_custom_client_pct
                                    ? 'text-emerald-900 bg-emerald-100/80 border border-emerald-300 font-bold'
                                    : 'text-emerald-700'
                                }`}
                                title={p.has_custom_client_pct ? 'Custom product override' : 'Category default rule'}
                              >
                                +{p.effective_client_pct}%
                              </span>
                            )}
                            {tierPerms.canViewCost && p.client_margin !== undefined && (
                              <span className="text-[10px] text-emerald-600/80 font-medium">({p.client_margin}%)</span>
                            )}
                          </div>
                        </td>
                      )}

                      <td className="px-3 py-3 text-center whitespace-nowrap">
                        <div className="inline-flex items-center justify-center gap-1">
                          {can('products.edit') && (
                            <button
                              type="button"
                              onClick={() => handleOpenQuickPct(p)}
                              title="Markup % Rules"
                              className="p-1.5 text-amber-700 hover:bg-amber-100/70 rounded-md border border-amber-200/90 transition"
                            >
                              <Percent className="w-3.5 h-3.5" />
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => handleOpenHistory(p)}
                            title="Monthly Revision History"
                            className="p-1.5 text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-md border border-slate-200 hover:border-emerald-200 transition"
                          >
                            <History className="w-3.5 h-3.5" />
                          </button>

                          {can('products.edit') && (
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(p)}
                              title="Edit Prices"
                              className="p-1.5 text-indigo-700 hover:bg-indigo-50 rounded-md border border-slate-200 hover:border-indigo-200 transition"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                          )}

                          {can('products.delete') && (
                            <button
                              type="button"
                              onClick={() => setConfirmDelete({ open: true, product: p, loading: false })}
                              title="Archive product"
                              className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-md border border-transparent hover:border-rose-200 transition"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              total={pagination.total}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Category Pricing Rules Modal */}
      <Modal
        isOpen={catRulesModalOpen}
        onClose={() => setCatRulesModalOpen(false)}
        title="Category Pricing Rules & Markup Defaults"
        description="Set the default markup percentages for each category. These apply to all products in the category unless an individual product has a custom override."
        maxWidth="2xl"
      >
        <form onSubmit={handleSaveCatRule} className="space-y-5">
          {/* Category Selector Tabs */}
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Select Category:</span>
            <button
              type="button"
              onClick={() => handleSelectCategoryForRule('ALL')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                selectedCatRule === 'ALL'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>All Products / Categories</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${selectedCatRule === 'ALL' ? 'bg-indigo-700 text-indigo-100' : 'bg-slate-200 text-slate-600'}`}>
                {categoryCounts.all || 59}
              </span>
            </button>
            {categoryRules.map(r => (
              <button
                key={r.category}
                type="button"
                onClick={() => handleSelectCategoryForRule(r.category)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                  selectedCatRule === r.category
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                <span>{r.category}</span>
                {r.product_count !== undefined && (
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${selectedCatRule === r.category ? 'bg-indigo-700 text-indigo-100' : 'bg-slate-200 text-slate-600'}`}>
                    {r.product_count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 text-xs text-slate-600 space-y-1">
            <div className="font-semibold text-slate-800 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-indigo-600" />
              <span>Calculation Hierarchy:</span>
            </div>
            <p>
              1. <strong>Base Cost (AED)</strong> &rarr; 2. <strong>Net Cost = Cost + {catRuleForm.net_cost_pct}%</strong> &rarr; 3. Selling Tiers calculated on top of <strong>Net Cost</strong>: Traders (+{catRuleForm.traders_pct}%), Sub Cont (+{catRuleForm.sub_cont_pct}%), Main Cont (+{catRuleForm.main_cont_pct}%), Client (+{catRuleForm.client_pct}%).
            </p>
          </div>

          {/* Percentage Configuration Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-3.5 bg-amber-50/70 border border-amber-200 rounded-xl space-y-2">
              <label className="text-xs font-bold text-amber-900 block flex items-center gap-1">
                <span>Net Cost Markup %</span>
                <span className="text-[10px] font-normal text-amber-700">(Calculated on Base Cost)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  required
                  value={catRuleForm.net_cost_pct}
                  onChange={e => setCatRuleForm(prev => ({ ...prev, net_cost_pct: e.target.value }))}
                  className="w-full px-3 py-2 pr-8 text-sm font-semibold rounded-lg border border-amber-300 focus:ring-2 focus:ring-amber-500 bg-white"
                />
                <span className="absolute right-3 top-2.5 text-xs text-slate-500 font-bold">%</span>
              </div>
              <p className="text-[11px] text-amber-800">
                Formula: <code>Cost &times; (1 + {catRuleForm.net_cost_pct}%)</code>
              </p>
            </div>

            <div className="p-3.5 bg-indigo-50/70 border border-indigo-200 rounded-xl space-y-2">
              <label className="text-xs font-bold text-indigo-900 block flex items-center gap-1">
                <span>Wholesale Traders %</span>
                <span className="text-[10px] font-normal text-indigo-700">(Calculated on Net Cost)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  required
                  value={catRuleForm.traders_pct}
                  onChange={e => setCatRuleForm(prev => ({ ...prev, traders_pct: e.target.value }))}
                  className="w-full px-3 py-2 pr-8 text-sm font-semibold rounded-lg border border-indigo-300 focus:ring-2 focus:ring-indigo-500 bg-white"
                />
                <span className="absolute right-3 top-2.5 text-xs text-slate-500 font-bold">%</span>
              </div>
              <p className="text-[11px] text-indigo-800">
                Formula: <code>Net Cost &times; (1 + {catRuleForm.traders_pct}%)</code>
              </p>
            </div>

            <div className="p-3.5 bg-indigo-50/70 border border-indigo-200 rounded-xl space-y-2">
              <label className="text-xs font-bold text-indigo-900 block flex items-center gap-1">
                <span>Sub Contractor %</span>
                <span className="text-[10px] font-normal text-indigo-700">(Calculated on Net Cost)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  required
                  value={catRuleForm.sub_cont_pct}
                  onChange={e => setCatRuleForm(prev => ({ ...prev, sub_cont_pct: e.target.value }))}
                  className="w-full px-3 py-2 pr-8 text-sm font-semibold rounded-lg border border-indigo-300 focus:ring-2 focus:ring-indigo-500 bg-white"
                />
                <span className="absolute right-3 top-2.5 text-xs text-slate-500 font-bold">%</span>
              </div>
              <p className="text-[11px] text-indigo-800">
                Formula: <code>Net Cost &times; (1 + {catRuleForm.sub_cont_pct}%)</code>
              </p>
            </div>

            <div className="p-3.5 bg-indigo-50/70 border border-indigo-200 rounded-xl space-y-2">
              <label className="text-xs font-bold text-indigo-900 block flex items-center gap-1">
                <span>Main Contractor %</span>
                <span className="text-[10px] font-normal text-indigo-700">(Calculated on Net Cost)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  required
                  value={catRuleForm.main_cont_pct}
                  onChange={e => setCatRuleForm(prev => ({ ...prev, main_cont_pct: e.target.value }))}
                  className="w-full px-3 py-2 pr-8 text-sm font-semibold rounded-lg border border-indigo-300 focus:ring-2 focus:ring-indigo-500 bg-white"
                />
                <span className="absolute right-3 top-2.5 text-xs text-slate-500 font-bold">%</span>
              </div>
              <p className="text-[11px] text-indigo-800">
                Formula: <code>Net Cost &times; (1 + {catRuleForm.main_cont_pct}%)</code>
              </p>
            </div>

            <div className="p-3.5 bg-emerald-50/70 border border-emerald-200 rounded-xl space-y-2 sm:col-span-2">
              <label className="text-xs font-bold text-emerald-900 block flex items-center gap-1">
                <span>Client Direct Retail %</span>
                <span className="text-[10px] font-normal text-emerald-700">(Calculated on Net Cost)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.1"
                  required
                  value={catRuleForm.client_pct}
                  onChange={e => setCatRuleForm(prev => ({ ...prev, client_pct: e.target.value }))}
                  className="w-full px-3 py-2 pr-8 text-sm font-semibold rounded-lg border border-emerald-300 focus:ring-2 focus:ring-emerald-500 bg-white"
                />
                <span className="absolute right-3 top-2.5 text-xs text-slate-500 font-bold">%</span>
              </div>
              <p className="text-[11px] text-emerald-800">
                Formula: <code>Net Cost &times; (1 + {catRuleForm.client_pct}%)</code>
              </p>
            </div>
          </div>

          {/* Example Calculation Box */}
          {(() => {
            const preview = calculateTierPrices(
              10,
              Number(catRuleForm.net_cost_pct || 0),
              Number(catRuleForm.traders_pct || 0),
              Number(catRuleForm.sub_cont_pct || 0),
              Number(catRuleForm.main_cont_pct || 0),
              Number(catRuleForm.client_pct || 0)
            );
            return (
              <div className="p-3 bg-slate-100 rounded-xl border border-slate-200">
                <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <span>Live Simulation (for a 10.00 AED Base Cost SKU):</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center text-xs">
                  <div className="p-1.5 bg-white rounded border">
                    <span className="text-[10px] text-slate-500 block">Net Cost (+{catRuleForm.net_cost_pct}%)</span>
                    <span className="font-mono font-bold text-amber-700">${preview.net_cost.toFixed(2)}</span>
                  </div>
                  <div className="p-1.5 bg-white rounded border">
                    <span className="text-[10px] text-slate-500 block">Traders (+{catRuleForm.traders_pct}%)</span>
                    <span className="font-mono font-bold text-indigo-700">${preview.traders_price.toFixed(2)}</span>
                  </div>
                  <div className="p-1.5 bg-white rounded border">
                    <span className="text-[10px] text-slate-500 block">Sub Cont (+{catRuleForm.sub_cont_pct}%)</span>
                    <span className="font-mono font-bold text-indigo-700">${preview.sub_cont_price.toFixed(2)}</span>
                  </div>
                  <div className="p-1.5 bg-white rounded border">
                    <span className="text-[10px] text-slate-500 block">Main Cont (+{catRuleForm.main_cont_pct}%)</span>
                    <span className="font-mono font-bold text-indigo-700">${preview.main_cont_price.toFixed(2)}</span>
                  </div>
                  <div className="p-1.5 bg-white rounded border">
                    <span className="text-[10px] text-slate-500 block">Client (+{catRuleForm.client_pct}%)</span>
                    <span className="font-mono font-bold text-emerald-700">${preview.client_price.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Recalculate Checkbox */}
          <div className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-100 flex items-start gap-2.5">
            <input
              type="checkbox"
              id="recalculate_cat_products"
              checked={catRuleForm.recalculate_products}
              onChange={e => setCatRuleForm(prev => ({ ...prev, recalculate_products: e.target.checked }))}
              className="mt-1 rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4"
            />
            <label htmlFor="recalculate_cat_products" className="text-xs text-indigo-950 font-medium cursor-pointer">
              <span className="font-bold">
                {selectedCatRule === 'ALL'
                  ? `Apply & Recalculate ALL ${categoryCounts.all || 59} products across all categories now`
                  : `Apply & Recalculate all products in "${selectedCatRule}" now`}
              </span>
              <p className="text-[11px] text-indigo-800 font-normal mt-0.5">
                Automatically recalculates Net Cost, Traders, Contractors, and Client prices for{' '}
                {selectedCatRule === 'ALL' ? `all ${categoryCounts.all || 59} catalog items` : 'all catalog items in this category'}{' '}
                that do not have custom overrides.
              </p>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
            <Button variant="outline" type="button" onClick={() => setCatRulesModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={savingCatRule}>
              {selectedCatRule === 'ALL'
                ? `Save Rules & Recalculate All Products (${categoryCounts.all || 59})`
                : `Save Category Rules (${selectedCatRule})`}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Quick Percentage Adjust Modal (Individual Product) */}
      <Modal
        isOpen={!!quickPctProduct}
        onClose={() => setQuickPctProduct(null)}
        title={quickPctProduct ? `Markup % Rules: ${quickPctProduct.name}` : 'Markup % Rules'}
        description={`Item #${quickPctProduct?.item_no || ''} • Category: ${quickPctProduct?.category || ''}`}
        maxWidth="lg"
      >
        {quickPctProduct && (() => {
          const catRule = getRuleForCategory(quickPctProduct.category);
          const costVal = Number(quickPctForm.cost || 0);
          const effNet = quickPctForm.net_cost_pct !== '' ? Number(quickPctForm.net_cost_pct) : catRule.net_cost_pct;
          const effTrader = quickPctForm.traders_pct !== '' ? Number(quickPctForm.traders_pct) : catRule.traders_pct;
          const effSub = quickPctForm.sub_cont_pct !== '' ? Number(quickPctForm.sub_cont_pct) : catRule.sub_cont_pct;
          const effMain = quickPctForm.main_cont_pct !== '' ? Number(quickPctForm.main_cont_pct) : catRule.main_cont_pct;
          const effClient = quickPctForm.client_pct !== '' ? Number(quickPctForm.client_pct) : catRule.client_pct;

          const preview = calculateTierPrices(costVal, effNet, effTrader, effSub, effMain, effClient);

          const minThreshold = Number(catRule.min_margin_pct ?? settings.min_margin_threshold_pct ?? 15);
          const costRef = settings.min_margin_cost_basis === 'cost' ? costVal : preview.net_cost;

          const calcMgn = (p: number) => (p > 0 && costRef > 0 ? Number((((p - costRef) / p) * 100).toFixed(1)) : 0);
          const traderMargin = calcMgn(preview.traders_price);
          const subMargin = calcMgn(preview.sub_cont_price);
          const mainMargin = calcMgn(preview.main_cont_price);
          const clientMargin = calcMgn(preview.client_price);

          const allMargins = [traderMargin, subMargin, mainMargin, clientMargin].filter(m => m > 0);
          const lowestQuickMargin = allMargins.length > 0 ? Math.min(...allMargins) : 0;
          const isQuickBreached = costRef > 0 && allMargins.some(m => m < minThreshold);
          const quickMarginStatus: 'healthy' | 'warning' | 'breach' = isQuickBreached
            ? 'breach'
            : lowestQuickMargin < minThreshold + 5
            ? 'warning'
            : 'healthy';

          return (
            <form onSubmit={handleSaveQuickPct} className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-xl border text-xs text-slate-600 flex items-center justify-between">
                <div>
                  <span className="font-semibold text-slate-800">Category Defaults ({quickPctProduct.category}):</span>
                  <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                    Net: +{catRule.net_cost_pct}% | Traders: +{catRule.traders_pct}% | Sub: +{catRule.sub_cont_pct}% | Main: +{catRule.main_cont_pct}% | Client: +{catRule.client_pct}%
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setQuickPctForm(prev => ({
                      ...prev,
                      net_cost_pct: '',
                      traders_pct: '',
                      sub_cont_pct: '',
                      main_cont_pct: '',
                      client_pct: '',
                    }));
                  }}
                  className="px-2.5 py-1 text-xs font-semibold text-indigo-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition flex items-center gap-1 shadow-xs"
                >
                  <RotateCcw className="w-3 h-3 text-indigo-600" />
                  <span>Use Category Defaults</span>
                </button>
              </div>

              {/* Base Cost (Editable if authorized) */}
              {tierPerms.canEditCost && (
                <div className="p-3 bg-amber-50/60 rounded-xl border border-amber-200/80">
                  <Input
                    label="Base Manufacturing Cost (AED)"
                    type="number"
                    step="0.01"
                    value={quickPctForm.cost}
                    onChange={e => setQuickPctForm(prev => ({ ...prev, cost: e.target.value }))}
                    helperText="Baseline for calculating Net Cost and selling tiers"
                  />
                </div>
              )}

              {/* Percentage Controls Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {tierPerms.canEditCost && (
                  <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-700">Net Cost % (on Cost)</label>
                      <span className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${quickPctForm.net_cost_pct !== '' ? 'bg-amber-100 text-amber-800 font-bold' : 'bg-slate-100 text-slate-500'}`}>
                        {quickPctForm.net_cost_pct !== '' ? 'Custom' : `Default (+${catRule.net_cost_pct}%)`}
                      </span>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        placeholder={String(catRule.net_cost_pct)}
                        value={quickPctForm.net_cost_pct}
                        onChange={e => setQuickPctForm(prev => ({ ...prev, net_cost_pct: e.target.value }))}
                        className="w-full px-3 py-1.5 pr-8 text-sm font-semibold rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500"
                      />
                      <span className="absolute right-3 top-2 text-xs text-slate-400 font-bold">%</span>
                    </div>
                    <span className="text-[11px] text-slate-500 block font-mono">
                      Calculated: <strong className="text-amber-800">${preview.net_cost.toFixed(2)}</strong>
                    </span>
                  </div>
                )}

                {tierPerms.canEditTraders && (
                  <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-700">Traders % (on Net)</label>
                      <span className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${quickPctForm.traders_pct !== '' ? 'bg-indigo-100 text-indigo-800 font-bold' : 'bg-slate-100 text-slate-500'}`}>
                        {quickPctForm.traders_pct !== '' ? 'Custom' : `Default (+${catRule.traders_pct}%)`}
                      </span>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        placeholder={String(catRule.traders_pct)}
                        value={quickPctForm.traders_pct}
                        onChange={e => setQuickPctForm(prev => ({ ...prev, traders_pct: e.target.value }))}
                        className="w-full px-3 py-1.5 pr-8 text-sm font-semibold rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500"
                      />
                      <span className="absolute right-3 top-2 text-xs text-slate-400 font-bold">%</span>
                    </div>
                    <span className="text-[11px] text-slate-500 block font-mono">
                      Calculated: <strong className="text-indigo-800">${preview.traders_price.toFixed(2)}</strong>
                    </span>
                  </div>
                )}

                {tierPerms.canEditSubCont && (
                  <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-700">Sub Cont % (on Net)</label>
                      <span className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${quickPctForm.sub_cont_pct !== '' ? 'bg-indigo-100 text-indigo-800 font-bold' : 'bg-slate-100 text-slate-500'}`}>
                        {quickPctForm.sub_cont_pct !== '' ? 'Custom' : `Default (+${catRule.sub_cont_pct}%)`}
                      </span>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        placeholder={String(catRule.sub_cont_pct)}
                        value={quickPctForm.sub_cont_pct}
                        onChange={e => setQuickPctForm(prev => ({ ...prev, sub_cont_pct: e.target.value }))}
                        className="w-full px-3 py-1.5 pr-8 text-sm font-semibold rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500"
                      />
                      <span className="absolute right-3 top-2 text-xs text-slate-400 font-bold">%</span>
                    </div>
                    <span className="text-[11px] text-slate-500 block font-mono">
                      Calculated: <strong className="text-indigo-800">${preview.sub_cont_price.toFixed(2)}</strong>
                    </span>
                  </div>
                )}

                {tierPerms.canEditMainCont && (
                  <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-700">Main Cont % (on Net)</label>
                      <span className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${quickPctForm.main_cont_pct !== '' ? 'bg-indigo-100 text-indigo-800 font-bold' : 'bg-slate-100 text-slate-500'}`}>
                        {quickPctForm.main_cont_pct !== '' ? 'Custom' : `Default (+${catRule.main_cont_pct}%)`}
                      </span>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        placeholder={String(catRule.main_cont_pct)}
                        value={quickPctForm.main_cont_pct}
                        onChange={e => setQuickPctForm(prev => ({ ...prev, main_cont_pct: e.target.value }))}
                        className="w-full px-3 py-1.5 pr-8 text-sm font-semibold rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500"
                      />
                      <span className="absolute right-3 top-2 text-xs text-slate-400 font-bold">%</span>
                    </div>
                    <span className="text-[11px] text-slate-500 block font-mono">
                      Calculated: <strong className="text-indigo-800">${preview.main_cont_price.toFixed(2)}</strong>
                    </span>
                  </div>
                )}

                {tierPerms.canEditClient && (
                  <div className="p-3 bg-white rounded-lg border border-slate-200 space-y-1 sm:col-span-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-700">Client Direct % (on Net)</label>
                      <span className={`text-[10px] px-1.5 py-0.2 rounded font-semibold ${quickPctForm.client_pct !== '' ? 'bg-emerald-100 text-emerald-800 font-bold' : 'bg-slate-100 text-slate-500'}`}>
                        {quickPctForm.client_pct !== '' ? 'Custom' : `Default (+${catRule.client_pct}%)`}
                      </span>
                    </div>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        placeholder={String(catRule.client_pct)}
                        value={quickPctForm.client_pct}
                        onChange={e => setQuickPctForm(prev => ({ ...prev, client_pct: e.target.value }))}
                        className="w-full px-3 py-1.5 pr-8 text-sm font-semibold rounded-lg border border-slate-300 focus:ring-2 focus:ring-emerald-500"
                      />
                      <span className="absolute right-3 top-2 text-xs text-slate-400 font-bold">%</span>
                    </div>
                    <span className="text-[11px] text-slate-500 block font-mono">
                      Calculated: <strong className="text-emerald-800">${preview.client_price.toFixed(2)}</strong>
                    </span>
                  </div>
                )}
              </div>

              {/* Price Impact Summary & Live Margin Health */}
              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
                    Calculated Selling Schedule & Margin Health:
                  </span>
                  {tierPerms.canViewCost && (
                    <span
                      className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                        quickMarginStatus === 'healthy'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : quickMarginStatus === 'warning'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-rose-50 text-rose-700 border-rose-200 animate-pulse'
                      }`}
                    >
                      {quickMarginStatus === 'healthy' ? (
                        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      ) : quickMarginStatus === 'warning' ? (
                        <AlertTriangle className="w-3 h-3 text-amber-600" />
                      ) : (
                        <AlertCircle className="w-3 h-3 text-rose-600" />
                      )}
                      <span>Floor: {minThreshold}% | Lowest: {lowestQuickMargin}%</span>
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono">
                  <div className="p-2 bg-white rounded-lg border border-slate-200">
                    <span className="text-[10px] text-slate-500 block uppercase">Wholesale Traders</span>
                    <strong className="text-slate-900">${preview.traders_price.toFixed(2)}</strong>
                    {tierPerms.canViewCost && (
                      <span className={`block text-[10px] font-sans font-semibold ${traderMargin < minThreshold ? 'text-rose-600 font-bold' : 'text-slate-500'}`}>
                        {traderMargin}% margin
                      </span>
                    )}
                  </div>
                  <div className="p-2 bg-white rounded-lg border border-slate-200">
                    <span className="text-[10px] text-slate-500 block uppercase">Sub Contractor</span>
                    <strong className="text-slate-900">${preview.sub_cont_price.toFixed(2)}</strong>
                    {tierPerms.canViewCost && (
                      <span className={`block text-[10px] font-sans font-semibold ${subMargin < minThreshold ? 'text-rose-600 font-bold' : 'text-slate-500'}`}>
                        {subMargin}% margin
                      </span>
                    )}
                  </div>
                  <div className="p-2 bg-white rounded-lg border border-slate-200">
                    <span className="text-[10px] text-slate-500 block uppercase">Main Contractor</span>
                    <strong className="text-slate-900">${preview.main_cont_price.toFixed(2)}</strong>
                    {tierPerms.canViewCost && (
                      <span className={`block text-[10px] font-sans font-semibold ${mainMargin < minThreshold ? 'text-rose-600 font-bold' : 'text-slate-500'}`}>
                        {mainMargin}% margin
                      </span>
                    )}
                  </div>
                  <div className="p-2 bg-emerald-50/60 rounded-lg border border-emerald-200">
                    <span className="text-[10px] text-emerald-800 block uppercase">Client Direct</span>
                    <strong className="text-emerald-900">${preview.client_price.toFixed(2)}</strong>
                    {tierPerms.canViewCost && (
                      <span className={`block text-[10px] font-sans font-semibold ${clientMargin < minThreshold ? 'text-rose-600 font-bold' : 'text-emerald-700'}`}>
                        {clientMargin}% margin
                      </span>
                    )}
                  </div>
                </div>

                {/* Low Margin Warning & Strict Block Override */}
                {isQuickBreached && (
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl space-y-2 text-xs">
                    <div className="flex items-start gap-2 text-rose-800 font-medium">
                      <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                      <div>
                        <strong className="font-bold">Minimum Margin Guardrail Alert:</strong> Price drops below the required {minThreshold}% minimum gross margin threshold.
                        {settings.min_margin_enforcement_mode === 'strict' && (
                          <span className="block text-rose-700 font-normal mt-0.5">
                            Strict enforcement is enabled. You must explicitly acknowledge and document an authorized override justification to proceed.
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="pt-2 border-t border-rose-200/80 space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer font-bold text-rose-900">
                        <input
                          type="checkbox"
                          checked={quickPctForm.allow_low_margin}
                          onChange={e => setQuickPctForm(prev => ({ ...prev, allow_low_margin: e.target.checked }))}
                          className="rounded text-rose-600 focus:ring-rose-500 h-4 w-4"
                        />
                        <span>Authorize Low-Margin Price Override</span>
                      </label>

                      {quickPctForm.allow_low_margin && (
                        <input
                          type="text"
                          required
                          placeholder="State reason for low margin (e.g. Strategic client tender, bulk volume agreement)..."
                          value={quickPctForm.margin_override_reason}
                          onChange={e => setQuickPctForm(prev => ({ ...prev, margin_override_reason: e.target.value }))}
                          className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-rose-300 bg-white placeholder-slate-400 focus:ring-2 focus:ring-rose-500"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                <Button variant="outline" type="button" onClick={() => setQuickPctProduct(null)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  loading={savingQuickPct}
                  disabled={isQuickBreached && settings.min_margin_enforcement_mode === 'strict' && (!quickPctForm.allow_low_margin || !quickPctForm.margin_override_reason.trim())}
                >
                  Save & Recalculate Prices
                </Button>
              </div>
            </form>
          );
        })()}
      </Modal>

      {/* Full Edit Pricing Modal */}
      <Modal
        isOpen={!!editProduct}
        onClose={() => setEditProduct(null)}
        title={editProduct ? `Update Pricing: ${editProduct.name}` : 'Update Pricing'}
        description="Fields you are not authorized to update are locked or hidden."
        maxWidth="lg"
      >
        {editProduct && (
          <form onSubmit={handleSavePricing} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Product Name"
                required
                value={editPrices.name}
                onChange={e => setEditPrices(prev => ({ ...prev, name: e.target.value }))}
              />
              <div className="grid grid-cols-2 gap-2">
                <Select
                  label="Category"
                  value={editPrices.category}
                  onChange={e => setEditPrices(prev => ({ ...prev, category: e.target.value }))}
                  options={[
                    { value: 'Manufacturing', label: 'Manufacturing' },
                    { value: 'Trading Products', label: 'Trading Products' },
                  ]}
                />
                <Input
                  label="Unit"
                  value={editPrices.unit}
                  onChange={e => setEditPrices(prev => ({ ...prev, unit: e.target.value }))}
                  placeholder="e.g. 20 LTR"
                />
              </div>
            </div>

            {/* Recalculate Button */}
            <div className="flex items-center justify-between p-2.5 bg-slate-50 border rounded-lg text-xs">
              <span className="text-slate-600">Want to recalculate prices based on formula percentages?</span>
              <button
                type="button"
                onClick={handleRecalculateEditPrices}
                className="px-2.5 py-1 text-xs font-bold text-indigo-700 bg-white border border-indigo-200 rounded hover:bg-indigo-50 transition flex items-center gap-1 shadow-xs"
              >
                <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                <span>Auto-Calculate Prices</span>
              </button>
            </div>

            {/* Confidential Cost Section */}
            {tierPerms.canEditCost ? (
              <div className="p-4 bg-amber-50/60 rounded-xl border border-amber-200/80 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                    <DollarSign className="w-4 h-4 text-amber-600" />
                    <span>Cost & Net Cost</span>
                  </h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Input
                    label="Manufacturing Cost (AED)"
                    type="number"
                    step="0.01"
                    value={editPrices.cost}
                    onChange={e => setEditPrices(prev => ({ ...prev, cost: e.target.value }))}
                  />
                  <Input
                    label="Net Cost % (on Cost)"
                    type="number"
                    step="0.1"
                    placeholder={`Default: ${getRuleForCategory(editPrices.category).net_cost_pct}%`}
                    value={editPrices.net_cost_pct}
                    onChange={e => setEditPrices(prev => ({ ...prev, net_cost_pct: e.target.value }))}
                  />
                  <Input
                    label="Net Cost (AED)"
                    type="number"
                    step="0.01"
                    value={editPrices.net_cost}
                    onChange={e => setEditPrices(prev => ({ ...prev, net_cost: e.target.value }))}
                    helperText="Base cost with internal markup"
                  />
                </div>
              </div>
            ) : tierPerms.canViewCost ? (
              <div className="p-3 bg-slate-50 rounded-lg border text-xs text-slate-500">
                Cost is read-only for your current role (Cost: {formatPrice(editProduct.cost)}, Net Cost: {formatPrice(editProduct.net_cost)})
              </div>
            ) : null}

            {/* Selling Price Tiers Section */}
            <div className="space-y-3 pt-2 border-t border-slate-100">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Customer & Contractor Selling Tiers (AED)
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {tierPerms.canEditTraders && (
                  <div className="space-y-1">
                    <Input
                      label="Traders Price (AED)"
                      type="number"
                      step="0.01"
                      value={editPrices.traders_price}
                      onChange={e => setEditPrices(prev => ({ ...prev, traders_price: e.target.value }))}
                    />
                    <div className="flex items-center gap-1 text-[11px] text-slate-500">
                      <span>Markup % on Net:</span>
                      <input
                        type="number"
                        step="0.1"
                        placeholder={String(getRuleForCategory(editPrices.category).traders_pct)}
                        value={editPrices.traders_pct}
                        onChange={e => setEditPrices(prev => ({ ...prev, traders_pct: e.target.value }))}
                        className="w-16 px-1.5 py-0.5 text-xs border rounded font-mono"
                      />
                      <span>%</span>
                    </div>
                  </div>
                )}

                {tierPerms.canEditSubCont && (
                  <div className="space-y-1">
                    <Input
                      label="Sub Contractor Price (AED)"
                      type="number"
                      step="0.01"
                      value={editPrices.sub_cont_price}
                      onChange={e => setEditPrices(prev => ({ ...prev, sub_cont_price: e.target.value }))}
                    />
                    <div className="flex items-center gap-1 text-[11px] text-slate-500">
                      <span>Markup % on Net:</span>
                      <input
                        type="number"
                        step="0.1"
                        placeholder={String(getRuleForCategory(editPrices.category).sub_cont_pct)}
                        value={editPrices.sub_cont_pct}
                        onChange={e => setEditPrices(prev => ({ ...prev, sub_cont_pct: e.target.value }))}
                        className="w-16 px-1.5 py-0.5 text-xs border rounded font-mono"
                      />
                      <span>%</span>
                    </div>
                  </div>
                )}

                {tierPerms.canEditMainCont && (
                  <div className="space-y-1">
                    <Input
                      label="Main Contractor Price (AED)"
                      type="number"
                      step="0.01"
                      value={editPrices.main_cont_price}
                      onChange={e => setEditPrices(prev => ({ ...prev, main_cont_price: e.target.value }))}
                    />
                    <div className="flex items-center gap-1 text-[11px] text-slate-500">
                      <span>Markup % on Net:</span>
                      <input
                        type="number"
                        step="0.1"
                        placeholder={String(getRuleForCategory(editPrices.category).main_cont_pct)}
                        value={editPrices.main_cont_pct}
                        onChange={e => setEditPrices(prev => ({ ...prev, main_cont_pct: e.target.value }))}
                        className="w-16 px-1.5 py-0.5 text-xs border rounded font-mono"
                      />
                      <span>%</span>
                    </div>
                  </div>
                )}

                {tierPerms.canEditClient && (
                  <div className="space-y-1">
                    <Input
                      label="Client Direct Price (AED)"
                      type="number"
                      step="0.01"
                      value={editPrices.client_price}
                      onChange={e => setEditPrices(prev => ({ ...prev, client_price: e.target.value }))}
                    />
                    <div className="flex items-center gap-1 text-[11px] text-slate-500">
                      <span>Markup % on Net:</span>
                      <input
                        type="number"
                        step="0.1"
                        placeholder={String(getRuleForCategory(editPrices.category).client_pct)}
                        value={editPrices.client_pct}
                        onChange={e => setEditPrices(prev => ({ ...prev, client_pct: e.target.value }))}
                        className="w-16 px-1.5 py-0.5 text-xs border rounded font-mono"
                      />
                      <span>%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Monthly Revision & Historical Audit Section */}
            <div className="p-3.5 bg-emerald-50/60 rounded-xl border border-emerald-200/80 space-y-3">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-emerald-900">
                <Calendar className="w-4 h-4 text-emerald-700" />
                <span>Monthly Price Revision & History Log</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="Effective Month (YYYY-MM)"
                  type="month"
                  value={editPrices.effective_month}
                  onChange={e => setEditPrices(prev => ({ ...prev, effective_month: e.target.value }))}
                  helperText="Pricing effective billing period"
                />
                <Input
                  label="Revision Reason / Notes"
                  value={editPrices.change_reason}
                  onChange={e => setEditPrices(prev => ({ ...prev, change_reason: e.target.value }))}
                  placeholder="e.g. October 2026 Monthly Price Revision"
                  helperText="Archived in price history audit"
                />
              </div>
            </div>

            {/* Margin Guardrails Live Assessment */}
            {tierPerms.canViewCost && (() => {
              const catRule = getRuleForCategory(editPrices.category);
              const minThreshold = Number(catRule.min_margin_pct ?? settings.min_margin_threshold_pct ?? 15);
              const costVal = Number(editPrices.cost || 0);
              const netCostVal = Number(editPrices.net_cost || 0);
              const costRef = settings.min_margin_cost_basis === 'cost' ? costVal : netCostVal;

              const tradersVal = Number(editPrices.traders_price || 0);
              const subVal = Number(editPrices.sub_cont_price || 0);
              const mainVal = Number(editPrices.main_cont_price || 0);
              const clientVal = Number(editPrices.client_price || 0);

              const calcMgn = (p: number) => (p > 0 && costRef > 0 ? Number((((p - costRef) / p) * 100).toFixed(1)) : 0);
              const traderMargin = calcMgn(tradersVal);
              const subMargin = calcMgn(subVal);
              const mainMargin = calcMgn(mainVal);
              const clientMargin = calcMgn(clientVal);

              const allMargins = [traderMargin, subMargin, mainMargin, clientMargin].filter(m => m > 0);
              const lowestMargin = allMargins.length > 0 ? Math.min(...allMargins) : 0;
              const isBreached = costRef > 0 && allMargins.some(m => m < minThreshold);
              const marginStatus: 'healthy' | 'warning' | 'breach' = isBreached
                ? 'breach'
                : lowestMargin < minThreshold + 5
                ? 'warning'
                : 'healthy';

              return (
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                      <Shield className="w-4 h-4 text-emerald-600" />
                      <span>Gross Margin Guardrails Assessment</span>
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                        marginStatus === 'healthy'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : marginStatus === 'warning'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-rose-50 text-rose-700 border-rose-200 animate-pulse'
                      }`}
                    >
                      {marginStatus === 'healthy' ? (
                        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      ) : marginStatus === 'warning' ? (
                        <AlertTriangle className="w-3 h-3 text-amber-600" />
                      ) : (
                        <AlertCircle className="w-3 h-3 text-rose-600" />
                      )}
                      <span>Floor: {minThreshold}% | Lowest: {lowestMargin}%</span>
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono">
                    <div className="p-2 bg-white rounded-lg border border-slate-200">
                      <span className="text-[10px] text-slate-500 block uppercase">Wholesale Traders</span>
                      <strong className="text-slate-900">${tradersVal.toFixed(2)}</strong>
                      <span className={`block text-[10px] font-sans font-semibold ${traderMargin < minThreshold ? 'text-rose-600 font-bold' : 'text-slate-500'}`}>
                        {traderMargin}% margin
                      </span>
                    </div>
                    <div className="p-2 bg-white rounded-lg border border-slate-200">
                      <span className="text-[10px] text-slate-500 block uppercase">Sub Contractor</span>
                      <strong className="text-slate-900">${subVal.toFixed(2)}</strong>
                      <span className={`block text-[10px] font-sans font-semibold ${subMargin < minThreshold ? 'text-rose-600 font-bold' : 'text-slate-500'}`}>
                        {subMargin}% margin
                      </span>
                    </div>
                    <div className="p-2 bg-white rounded-lg border border-slate-200">
                      <span className="text-[10px] text-slate-500 block uppercase">Main Contractor</span>
                      <strong className="text-slate-900">${mainVal.toFixed(2)}</strong>
                      <span className={`block text-[10px] font-sans font-semibold ${mainMargin < minThreshold ? 'text-rose-600 font-bold' : 'text-slate-500'}`}>
                        {mainMargin}% margin
                      </span>
                    </div>
                    <div className="p-2 bg-emerald-50/60 rounded-lg border border-emerald-200">
                      <span className="text-[10px] text-emerald-800 block uppercase">Client Direct</span>
                      <strong className="text-emerald-900">${clientVal.toFixed(2)}</strong>
                      <span className={`block text-[10px] font-sans font-semibold ${clientMargin < minThreshold ? 'text-rose-600 font-bold' : 'text-emerald-700'}`}>
                        {clientMargin}% margin
                      </span>
                    </div>
                  </div>

                  {isBreached && (
                    <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl space-y-2 text-xs">
                      <div className="flex items-start gap-2 text-rose-800 font-medium">
                        <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                        <div>
                          <strong className="font-bold">Minimum Margin Guardrail Alert:</strong> Price drops below the required {minThreshold}% minimum gross margin floor.
                          {settings.min_margin_enforcement_mode === 'strict' && (
                            <span className="block text-rose-700 font-normal mt-0.5">
                              Strict enforcement is active. You must explicitly authorize and provide an audit justification.
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="pt-2 border-t border-rose-200/80 space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer font-bold text-rose-900">
                          <input
                            type="checkbox"
                            checked={editPrices.allow_low_margin}
                            onChange={e => setEditPrices(prev => ({ ...prev, allow_low_margin: e.target.checked }))}
                            className="rounded text-rose-600 focus:ring-rose-500 h-4 w-4"
                          />
                          <span>Authorize Low-Margin Price Override</span>
                        </label>

                        {editPrices.allow_low_margin && (
                          <input
                            type="text"
                            required
                            placeholder="State reason for low margin (e.g. Approved exception for volume client)..."
                            value={editPrices.margin_override_reason}
                            onChange={e => setEditPrices(prev => ({ ...prev, margin_override_reason: e.target.value }))}
                            className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-rose-300 bg-white placeholder-slate-400 focus:ring-2 focus:ring-rose-500"
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button variant="outline" type="button" onClick={() => setEditProduct(null)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" loading={savingPrice}>
                Save Pricing
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Add Product Modal */}
      <Modal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Create New Product"
        description="Add a new catalog item. Prices are automatically calculated using category markup rules."
        maxWidth="lg"
      >
        <form onSubmit={handleAddProduct} className="space-y-4">
          <Input
            label="Product Name"
            required
            value={newProduct.name}
            onChange={e => setNewProduct(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g. RBE 6020 20 LTR"
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Category"
              value={newProduct.category}
              onChange={e => handleAddProductCostChange(newProduct.cost, e.target.value)}
              options={[
                { value: 'Manufacturing', label: 'Manufacturing' },
                { value: 'Trading Products', label: 'Trading Products' },
              ]}
            />
            <Input
              label="Unit of Measure"
              value={newProduct.unit}
              onChange={e => setNewProduct(prev => ({ ...prev, unit: e.target.value }))}
              placeholder="e.g. 20 LTR, 20 KG"
            />
          </div>

          {tierPerms.canEditCost && (
            <div className="grid grid-cols-2 gap-4 p-3 bg-amber-50/50 rounded-lg border border-amber-100">
              <Input
                label="Manufacturing Cost (AED)"
                type="number"
                step="0.01"
                value={newProduct.cost}
                onChange={e => handleAddProductCostChange(e.target.value)}
                placeholder="Enter base cost..."
              />
              <Input
                label="Net Cost (AED) [Auto]"
                type="number"
                step="0.01"
                value={newProduct.net_cost}
                onChange={e => setNewProduct(prev => ({ ...prev, net_cost: e.target.value }))}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Traders Price (AED)"
              type="number"
              step="0.01"
              value={newProduct.traders_price}
              onChange={e => setNewProduct(prev => ({ ...prev, traders_price: e.target.value }))}
            />
            <Input
              label="Sub Contractor Price (AED)"
              type="number"
              step="0.01"
              value={newProduct.sub_cont_price}
              onChange={e => setNewProduct(prev => ({ ...prev, sub_cont_price: e.target.value }))}
            />
            <Input
              label="Main Contractor Price (AED)"
              type="number"
              step="0.01"
              value={newProduct.main_cont_price}
              onChange={e => setNewProduct(prev => ({ ...prev, main_cont_price: e.target.value }))}
            />
            <Input
              label="Client Price (AED)"
              type="number"
              step="0.01"
              value={newProduct.client_price}
              onChange={e => setNewProduct(prev => ({ ...prev, client_price: e.target.value }))}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button variant="outline" type="button" onClick={() => setAddModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={addingProduct}>
              Create Product
            </Button>
          </div>
        </form>
      </Modal>

      {/* Price Revision History Modal */}
      <Modal
        isOpen={!!historyProduct}
        onClose={() => setHistoryProduct(null)}
        title={historyProduct ? `Price Revision History: ${historyProduct.name}` : 'Price Revision History'}
        description={`Item #${historyProduct?.item_no || ''} • Category: ${historyProduct?.category || ''} • Unit: ${historyProduct?.unit || 'N/A'}`}
        maxWidth="2xl"
      >
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
          {historyLoading ? (
            <div className="py-12 text-center text-slate-400 space-y-2">
              <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs">Loading historical revisions...</p>
            </div>
          ) : historyRecords.length === 0 ? (
            <div className="py-8 text-center text-slate-500 text-sm">
              No historical price revisions found for this item.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs text-slate-500 pb-2 border-b border-slate-100">
                <span className="font-semibold text-slate-700">{historyRecords.length} Historical Snapshot{historyRecords.length > 1 ? 's' : ''}</span>
                <span>Sorted by Most Recent</span>
              </div>

              {historyRecords.map((rec, idx) => {
                const prevRec = historyRecords[idx + 1];
                const clientDiff = prevRec && prevRec.client_price && rec.client_price
                  ? Number((((rec.client_price - prevRec.client_price) / prevRec.client_price) * 100).toFixed(1))
                  : null;

                return (
                  <div key={rec.id} className="p-4 rounded-xl border border-slate-200 bg-white shadow-sm hover:border-emerald-300 transition">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2 pb-2 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                          {rec.effective_month}
                        </span>
                        <span className="text-xs font-semibold text-slate-800">
                          {rec.change_reason || 'Monthly Price Revision'}
                        </span>
                        {tierPerms.canViewCost && rec.is_low_margin && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200"
                            title={rec.margin_override_reason || 'Low margin authorized'}
                          >
                            <AlertCircle className="w-2.5 h-2.5 text-rose-600" />
                            <span>Low Margin Exception</span>
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-400 flex items-center gap-2">
                        <span>{formatDateTime(rec.created_at, settings.date_format)}</span>
                        {rec.changed_by_name && (
                          <span className="font-medium text-slate-600">by {rec.changed_by_name}</span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-xs">
                      {tierPerms.canViewCost && (
                        <>
                          <div className="p-2 rounded-lg bg-amber-50/50 border border-amber-100">
                            <span className="text-[10px] text-amber-800 font-semibold uppercase block">Cost</span>
                            <span className="font-mono font-bold text-slate-800">{formatPrice(rec.cost)}</span>
                          </div>
                          <div className="p-2 rounded-lg bg-amber-50/50 border border-amber-100">
                            <span className="text-[10px] text-amber-800 font-semibold uppercase block">Net Cost</span>
                            <span className="font-mono font-bold text-slate-800">{formatPrice(rec.net_cost)}</span>
                          </div>
                        </>
                      )}

                      {tierPerms.canViewTraders && (
                        <div className="p-2 rounded-lg bg-slate-50 border border-slate-100">
                          <span className="text-[10px] text-slate-500 font-semibold uppercase block">Traders</span>
                          <span className="font-mono font-bold text-slate-800">{formatPrice(rec.traders_price)}</span>
                        </div>
                      )}

                      {tierPerms.canViewSubCont && (
                        <div className="p-2 rounded-lg bg-slate-50 border border-slate-100">
                          <span className="text-[10px] text-slate-500 font-semibold uppercase block">Sub Cont</span>
                          <span className="font-mono font-bold text-slate-800">{formatPrice(rec.sub_cont_price)}</span>
                        </div>
                      )}

                      {tierPerms.canViewMainCont && (
                        <div className="p-2 rounded-lg bg-slate-50 border border-slate-100">
                          <span className="text-[10px] text-slate-500 font-semibold uppercase block">Main Cont</span>
                          <span className="font-mono font-bold text-slate-800">{formatPrice(rec.main_cont_price)}</span>
                        </div>
                      )}

                      {tierPerms.canViewClient && (
                        <div className="p-2 rounded-lg bg-emerald-50/50 border border-emerald-100">
                          <span className="text-[10px] text-emerald-800 font-semibold uppercase block">Client</span>
                          <span className="font-mono font-bold text-emerald-700">{formatPrice(rec.client_price)}</span>
                          {clientDiff !== null && (
                            <span className={`block text-[10px] font-semibold ${clientDiff > 0 ? 'text-emerald-600' : clientDiff < 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                              {clientDiff > 0 ? `+${clientDiff}%` : `${clientDiff}%`}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Product Confirmation */}
      <ConfirmDialog
        isOpen={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, product: null, loading: false })}
        onConfirm={handleDeleteProduct}
        title="Confirm Product Deletion"
        message={`Are you sure you want to archive "${confirmDelete.product?.name}"?`}
        confirmLabel="Archive Product"
        loading={confirmDelete.loading}
      />
    </div>
  );
};
